import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useEffect, useState } from "react";
import { AppModal } from "global/components/AppModal";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { fetchAttributeDefaults, updateSsoConnector } from "./SsoConfigService";
import {
  AttributeMappingErrors,
  IDENTITY_ATTRIBUTE_FIELDS,
  IdentityAttributeKey,
  IdentityAttributeMapping,
  SourceMode,
  SsoConnection,
  SsoConnectionInput,
  validateAttributeMapping,
} from "./SsoConfigTypes";
import AttributeMappingEditor from "./AttributeMappingEditor";
import {
  attrMappingBannerSx,
  attrMappingHeaderIconSx,
  planBlockedDescSx,
  planBlockedIconSx,
  planBlockedTitleSx,
} from "./EnterpriseSSO.styles";

interface AttributeMappingPanelProps {
  open: boolean;
  connection: SsoConnection | null;
  onClose: () => void;
  onSaved: () => void;
}

type DialogTab = "attributes" | "roles";

type ModeState = Record<IdentityAttributeKey, SourceMode>;

const ALL_KEYS = IDENTITY_ATTRIBUTE_FIELDS.map((field) => field.key);

/** A field is Custom exactly when the connector already carries paths for it. */
const deriveModes = (mapping: IdentityAttributeMapping): ModeState =>
  ALL_KEYS.reduce((modes, key) => {
    modes[key] = (mapping[key] ?? []).length > 0 ? "custom" : "default";
    return modes;
  }, {} as ModeState);

const sanitize = (mapping: IdentityAttributeMapping): IdentityAttributeMapping => {
  const cleaned: IdentityAttributeMapping = {};
  (Object.keys(mapping) as IdentityAttributeKey[]).forEach((key) => {
    const paths = (mapping[key] ?? []).map((path) => path.trim()).filter((path) => path.length > 0);
    if (paths.length > 0) {
      cleaned[key] = paths;
    }
  });
  return cleaned;
};

const AttributeMappingPanel = ({
  open,
  connection,
  onClose,
  onSaved,
}: AttributeMappingPanelProps) => {
  const [tab, setTab] = useState<DialogTab>("attributes");
  const [mapping, setMapping] = useState<IdentityAttributeMapping>({});
  const [enabledFields, setEnabledFields] = useState<IdentityAttributeKey[]>([]);
  // Mode and expansion live here, not in the editor: switching to the Roles tab unmounts the
  // editor, and a field the admin just set to Custom must not silently snap back to Default.
  const [sourceMode, setSourceMode] = useState<ModeState>(() => deriveModes({}));
  const [expandedFields, setExpandedFields] = useState<
    Partial<Record<IdentityAttributeKey, boolean>>
  >({});
  const [errors, setErrors] = useState<AttributeMappingErrors>({});
  const [defaults, setDefaults] = useState<
    Partial<Record<IdentityAttributeKey, string[]>>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!open || !connection) return;
    const initialMapping = connection.attributeMapping ?? {};
    const initialEnabled = connection.syncEnabledFields ?? [];
    setTab("attributes");
    setMapping(initialMapping);
    setEnabledFields(initialEnabled);
    setSourceMode(deriveModes(initialMapping));
    // Enabled rows open on load so an admin sees what is configured without hunting through
    // chevrons; everything off stays collapsed to its single line. Email always starts expanded —
    // it's the one field every connector needs to get right.
    setExpandedFields(
      initialEnabled.reduce(
        (expanded, key) => ({ ...expanded, [key]: true }),
        { email: true } as Partial<Record<IdentityAttributeKey, boolean>>,
      ),
    );
    setErrors({});
    const loadDefaults = async () => {
      setIsLoading(true);
      try {
        setDefaults(await fetchAttributeDefaults(connection.protocol));
      } catch (error: any) {
        // Non-fatal: without defaults the editor still works, it just cannot show the
        // admin what we already try before they override it.
        setDefaults({});
      } finally {
        setIsLoading(false);
      }
    };
    loadDefaults();
  }, [open, connection]);

  const handleMappingChange = (next: IdentityAttributeMapping) => {
    setMapping(next);
    setErrors({});
  };

  const handleSourceModeChange = (key: IdentityAttributeKey, mode: SourceMode) => {
    setSourceMode((prev) => ({ ...prev, [key]: mode }));
    setErrors({});
  };

  const handleSave = async () => {
    if (!connection) return;

    const cleaned = sanitize(mapping);
    const customFields = ALL_KEYS.filter(
      (key) =>
        sourceMode[key] === "custom" &&
        // A sync field switched to Custom and then turned back off is not worth blocking a save
        // over — nothing it maps will be read.
        (key === "email" || enabledFields.includes(key)),
    );
    const found = validateAttributeMapping(
      cleaned,
      connection.protocol,
      customFields,
      sourceMode,
      defaults,
    );
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setExpandedFields((prev) => {
        const next = { ...prev };
        (Object.keys(found) as IdentityAttributeKey[]).forEach((key) => {
          next[key] = true;
        });
        return next;
      });
      return;
    }

    setIsSaving(true);
    try {
      const body: SsoConnectionInput = {
        emailDomains: connection.emailDomains,
        emailClaim: connection.emailClaim ?? undefined,
        attributeMapping: Object.keys(cleaned).length > 0 ? cleaned : null,
        syncEnabledFields: enabledFields,
        enforceSso: connection.enforceSso,
        pwdFallbackUserId: connection.pwdFallbackUserId ?? undefined,
        sendLoginHint: connection.sendLoginHint,
        autoProvisionUsers: connection.autoProvisionUsers,
        defaultRoleId: connection.defaultRoleId ?? undefined,
        status: connection.status,
      };
      await updateSsoConnector(connection.registrationId, body);
      openSuccessNotification("Attribute mapping saved.");
      onSaved();
      onClose();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setMapping({});
    setSourceMode(deriveModes({}));
    setErrors({});
  };

  const renderAttributesTab = () => (
    <Stack spacing={2.5}>
      <Alert severity="info" sx={attrMappingBannerSx}>
        These mappings are used to find or create users and to sync profile data on login.
      </Alert>

      {errors.form && <Alert severity="error">{errors.form}</Alert>}

      <AttributeMappingEditor
        value={mapping}
        defaults={defaults}
        enabledFields={enabledFields}
        sourceMode={sourceMode}
        expandedFields={expandedFields}
        errors={errors}
        onChange={handleMappingChange}
        onEnabledFieldsChange={setEnabledFields}
        onSourceModeChange={handleSourceModeChange}
        onExpandedFieldsChange={setExpandedFields}
        disabled={isSaving}
      />
    </Stack>
  );

  const renderRolesTab = () => (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 5, px: 3 }}>
      <Box sx={planBlockedIconSx}>
        <SwapHorizOutlinedIcon />
      </Box>
      <Typography sx={planBlockedTitleSx}>Role mapping is coming soon</Typography>
      <Typography sx={planBlockedDescSx} textAlign="center" maxWidth="420px">
        You&apos;ll be able to map groups or roles asserted by {connection?.protocol ?? "your"}{" "}
        identity provider onto app roles here, so access stays in sync without manual
        assignment. For now, new users get the default role configured on this connector.
      </Typography>
    </Stack>
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      // AppModal draws the tinted tile, heading and sub-line itself, so the
      // hand-built header stack this used to pass as `title` is unpicked into
      // the three slots it was imitating.
      icon={<BadgeOutlinedIcon sx={{ fontSize: 20 }} />}
      title={`Attribute mapping — ${
        connection?.slug || connection?.registrationId || ""
      }`}
      subtitle={`Map attributes from your ${connection?.protocol} identity provider to local profile fields.`}
      size="md"
      busy={isSaving}
      /* Low-emphasis and destructive-ish, so it sits apart from the pair. */
      footerStart={
        <Button
          variant="text"
          size="small"
          startIcon={<RestartAltIcon fontSize="small" />}
          onClick={handleReset}
          disabled={isSaving || isLoading}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Reset to defaults
        </Button>
      }
      primaryLabel="Save changes"
      onPrimary={handleSave}
      primaryDisabled={isLoading}
      primaryLoading={isSaving}
      cancelLabel="Cancel"
      onCancel={onClose}
    >
        <Box sx={{ pt: 1 }}>
          <CustomLoader isLoading={isLoading || isSaving} />
          <Tabs
            value={tab}
            onChange={(_event, next) => setTab(next)}
            sx={{ mb: 2.5, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 600 } }}
          >
            <Tab value="attributes" label="Attributes" />
            <Tab value="roles" label="Roles" />
          </Tabs>
          {tab === "attributes" ? renderAttributesTab() : renderRolesTab()}
        </Box>
    </AppModal>
  );
};

export default AttributeMappingPanel;
